import { Context, useContext } from 'react';

/**
 * Usage:
 *  // use this:
 *  const { user } = useNonNullContext(userContext);
 *  // instead of this: (note the non-null assertion that is required to use the straight up `useContext` hook)
 *  const { user } = useContext(userContext)!;
 *
 * We're always providing context values to providers like UserContext and ConfigContext; however, as we are forced
 * to provide an initial value and we fetch these values from backend, the type for each of these is by necessity a
 * union with `null`.
 *
 * Casting the intial default value to context object is riskier than explicitly checking here. We can use this hook
 * safely in place of `useContext` to avoid having to do non-null assertions or check for null whenever we consume
 * the context.
 *
 * Taken from https://stackoverflow.com/questions/63080452/react-createcontextnull-not-allowed-with-typescript
 */
const useNonNullContext = <T>(context: Context<T | null>): T => {
  const contextValue = useContext(context);
  if (contextValue === null) {
    throw Error('Context has not been Provided!');
  }
  return contextValue;
};

export default useNonNullContext;
